// AGC: Analytics init module — runs once at app boot to wire up subscribers
// AGC: Handles: static user properties, auth state changes, language changes, online/offline flush
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from './firebase'
import i18n from '../i18n'
import { setUser, setUserProp, flushQueue, setReleaseVersion } from './analytics'
import type { BuildContext, Language } from './analytics-events'

// AGC: App version injected at build time from root package.json via vite.config.ts
const APP_VERSION: string = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown'

// AGC: Bucket raw days into coarse ranges to avoid high-cardinality user properties
function bucketAccountAge(days: number): string {
  if (days <= 0) return '0'
  if (days <= 7) return '1-7'
  if (days <= 30) return '8-30'
  if (days <= 90) return '31-90'
  return '90+'
}

// AGC: Normalize legacy/unknown role strings to the three analytics-safe UserRole values
function normalizeUserRole(raw: string | undefined): 'owner' | 'manager' | 'worker' {
  if (raw === 'owner' || raw === 'manager' || raw === 'worker') return raw
  if (raw === 'admin') return 'owner'
  return 'worker'
}

// AGC: Dynamically import getUserProfile to avoid circular dependency with Firestore init
async function hydrateDynamicUserProps(uid: string): Promise<void> {
  const { getUserProfile } = await import('../services/userProfileService')
  const profile = await getUserProfile(uid)
  if (!profile) return

  const role = normalizeUserRole(profile.role)
  await setUserProp('user_role', role)

  if (profile.createdAt) {
    const created = new Date(profile.createdAt).getTime()
    const days = Math.floor((Date.now() - created) / 86_400_000)
    await setUserProp('account_age_days', bucketAccountAge(days))
  }
}

// AGC: Main init function — call once before ReactDOM.createRoot, fire-and-forget with void
export async function initAnalytics(): Promise<void> {
  // AGC: Set release version in shared context — appears on every event
  setReleaseVersion(APP_VERSION)

  // AGC: Static user properties — set once at startup, before any event is fired
  const buildContext = (import.meta.env.VITE_APP_CONTEXT as BuildContext | undefined) ?? 'desktop'
  await setUserProp('build_context', buildContext)
  await setUserProp('app_version', APP_VERSION)

  // AGC: Auth subscriber — updates user_id on login/logout + hydrates dynamic user properties
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      await setUser(null)
      return
    }
    await setUser(user.uid)
    void hydrateDynamicUserProps(user.uid).catch((err) => {
      // AGC: Non-fatal — profile hydration failure must not block auth flow
      console.warn('[analytics] failed to hydrate user props', err)
    })
  })

  // AGC: Language subscriber — updates language user property whenever i18n switches locale
  i18n.on('languageChanged', (lng: string) => {
    // AGC: Strip region suffix (e.g. "pt-BR" → "pt") before casting to Language type
    const normalized = (lng.split('-')[0] as Language) ?? 'pt'
    void setUserProp('language', normalized)
  })

  // AGC: Online subscriber — flush buffered events when connectivity returns
  window.addEventListener('online', () => {
    void flushQueue()
  })

  // AGC: Flush on startup in case app was launched after being offline
  if (navigator.onLine) {
    void flushQueue()
  }
}
